import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Plus, Calendar, Check, X, Clock, Plane, Building2, IndianRupee, Upload, Download, FileText, ChevronDown, ChevronRight, Paperclip, Pencil, Lock, RotateCcw, Navigation, Trash2, Eye, BarChart3, Users, AlertTriangle, ShieldAlert } from 'lucide-react';
import api from '@/lib/api';
import { getUser, isAdmin, isHR } from '@/lib/auth';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import JourneyReportsTab from './journey/JourneyReportsTab';
import AllowanceMasterTab from './journey/AllowanceMasterTab';

const CHART_COLORS = ['#0D2850','#059669','#b45309','#7c3aed','#dc2626','#0284c7','#be185d','#4f46e5','#d97706','#10b981'];
const TIP_STYLE = { contentStyle: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, padding: '8px 12px' } };

const STATUS_COLORS = {
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  completed: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  pending_advance: 'bg-orange-100 text-orange-800',
  advance_released: 'bg-teal-100 text-teal-800',
};

function CalendarGrid({ month, fieldDays, officeDays }) {
  const [year, mon] = month.split('-').map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const firstDay = new Date(year, mon - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div data-testid="journey-calendar-grid">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} className="text-xs font-medium text-slate-500 text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dateStr = `${year}-${String(mon).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isField = fieldDays.includes(dateStr);
          const isOffice = officeDays.includes(dateStr);
          const isSunday = new Date(year, mon-1, day).getDay() === 0;
          let bg = 'bg-white';
          if (isField) bg = 'bg-blue-100 border-blue-300';
          else if (isOffice) bg = 'bg-emerald-50 border-emerald-300';
          else if (isSunday) bg = 'bg-slate-50 border-slate-200';
          return (
            <div key={day} className={`h-10 sm:h-12 rounded-lg border text-center flex flex-col items-center justify-center text-xs ${bg}`}>
              <span className={`font-medium ${isSunday ? 'text-red-500' : 'text-slate-700'}`}>{day}</span>
              {isField && <Plane className="w-3 h-3 text-blue-600" />}
              {isOffice && !isField && <Building2 className="w-3 h-3 text-emerald-600" />}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3 text-xs">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" /> Field</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-300" /> Office</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-50 border border-slate-200" /> Off</div>
      </div>
    </div>
  );
}

const fmt = v => `\u20B9${(v || 0).toLocaleString('en-IN')}`;

export default function JourneyPlanPage() {
  const user = getUser();
  const admin = isAdmin() || isHR();
  const [canApprove, setCanApprove] = useState(false);
  const [canConfirmAdvance, setCanConfirmAdvance] = useState(false);
  const [tab, setTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [calData, setCalData] = useState({ field_days: [], office_days: [], plans: [] });
  const [employees, setEmployees] = useState([]);
  const [allowances, setAllowances] = useState({ all: {}, my_designation: null, my_allowance: null, my_category: null });
  const [month, setMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });
  const [planOpen, setPlanOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [editExpOpen, setEditExpOpen] = useState(false);
  const [editExpForm, setEditExpForm] = useState({ items: [], remarks: '', plan_id: '' });
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [activeExpense, setActiveExpense] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [planSummary, setPlanSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const receiptRef = useRef(null);
  const [planForm, setPlanForm] = useState({ title: '', purpose: '', start_date: '', end_date: '', destinations: '', employee_id: '', advance_amount: '', customer_name: '', company_name: '', contact_number: '', remarks: '' });
  const [expForm, setExpForm] = useState({ plan_id: '', expense_date: new Date().toISOString().slice(0, 10), items: [{ description: '', category: 'travel', amount: '' }], remarks: '' });
  const [expFiles, setExpFiles] = useState([]);
  const expFileRef = useRef(null);
  const [planFilter, setPlanFilter] = useState({ employee_id: '', status: '' });
  const [expFilter, setExpFilter] = useState({ plan_id: '', employee_id: '', status: '' });
  const [visits, setVisits] = useState([]);
  const [visitOpen, setVisitOpen] = useState(false);
  const [visitForm, setVisitForm] = useState({ client_name: '', company_name: '', customer_name: '', contact_number: '', city_town: '', visit_date: '', visit_time: '', purpose: '', remarks: '', plan_id: '', location: {} });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [visitAnalytics, setVisitAnalytics] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [canApproveExceptions, setCanApproveExceptions] = useState(false);
  const [settlements, setSettlements] = useState([]);

  const loadVisits = useCallback(async () => {
    try { setVisits((await api.get('/api/journey/visits')).data); } catch {}
  }, []);

  const loadVisitAnalytics = useCallback(async () => {
    if (!admin) return;
    try { setVisitAnalytics((await api.get('/api/journey/visit-analytics')).data); } catch {}
  }, [admin]);

  const loadExceptions = useCallback(async () => {
    try {
      const { data } = await api.get('/api/journey/exceptions');
      setExceptions(data.exceptions || []);
      setCanApproveExceptions(data.can_approve || false);
    } catch { /* non-admin employees won't have access */ }
  }, []);

  const loadSettlements = useCallback(async () => {
    try { setSettlements((await api.get('/api/journey/settlements')).data); } catch {}
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const params = {};
      if (planFilter.employee_id) params.employee_id = planFilter.employee_id;
      if (planFilter.status) params.status = planFilter.status;
      setPlans((await api.get('/api/journey/plans', { params })).data);
    } catch {}
  }, [planFilter]);
  const loadExpenses = useCallback(async () => {
    try {
      const params = {};
      if (expFilter.plan_id) params.plan_id = expFilter.plan_id;
      if (expFilter.employee_id) params.employee_id = expFilter.employee_id;
      if (expFilter.status) params.status = expFilter.status;
      setExpenses((await api.get('/api/journey/expenses', { params })).data);
    } catch {}
  }, [expFilter]);
  const loadCalendar = useCallback(async () => {
    try { setCalData((await api.get('/api/journey/calendar', { params: { month } })).data); } catch {}
  }, [month]);
  const loadMeta = useCallback(async () => {
    try {
      const [eRes, aRes] = await Promise.all([
        admin ? api.get('/api/employees').then(r => ({ data: r.data.employees || r.data || [] })) : Promise.resolve({ data: [] }),
        api.get('/api/journey/allowances')
      ]);
      setEmployees(eRes.data); setAllowances(aRes.data);
      // Check if current user is a manager/director (can approve plans)
      if (user?.employee_id) {
        try {
          const { data: myEmp } = await api.get(`/api/employees/${user.employee_id}`);
          const emp = myEmp.employee || myEmp;
          const role = emp.org_role || '';
          if (['manager', 'director', 'director_hr', 'hr'].includes(role) || admin) {
            setCanApprove(true);
          }
          if (emp.department?.toLowerCase() === 'accounts' || admin) {
            setCanConfirmAdvance(true);
          }
        } catch {}
      } else if (admin) {
        setCanApprove(true);
        setCanConfirmAdvance(true);
      }
    } catch {}
  }, [admin, user?.employee_id]);

  useEffect(() => { loadPlans(); loadExpenses(); loadMeta(); loadVisits(); loadVisitAnalytics(); loadExceptions(); loadSettlements(); }, [loadPlans, loadExpenses, loadMeta, loadVisits, loadVisitAnalytics, loadExceptions, loadSettlements]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  // Compute expense totals per plan
  const planExpenseTotals = {};
  expenses.forEach(e => {
    if (e.plan_id) {
      if (!planExpenseTotals[e.plan_id]) planExpenseTotals[e.plan_id] = { count: 0, total: 0 };
      planExpenseTotals[e.plan_id].count += 1;
      planExpenseTotals[e.plan_id].total += e.total_amount || 0;
    }
  });

  const handleCreatePlan = async () => {
    if (!planForm.start_date || !planForm.end_date || !planForm.title) { toast.error('Fill title, start & end dates'); return; }
    try {
      await api.post('/api/journey/plans', {
        ...planForm,
        destinations: planForm.destinations.split(',').map(d => d.trim()).filter(Boolean),
        employee_id: planForm.employee_id || undefined,
        advance_amount: planForm.advance_amount ? parseFloat(planForm.advance_amount) : 0,
      });
      toast.success('Journey plan created'); setPlanOpen(false);
      setPlanForm({ title: '', purpose: '', start_date: '', end_date: '', destinations: '', employee_id: '', advance_amount: '', customer_name: '', company_name: '', contact_number: '', remarks: '' });
      loadPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApprovePlan = async (id, action) => {
    try {
      await api.post(`/api/journey/plans/${id}/approve`, { action });
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      loadPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleConfirmAdvance = async (id) => {
    try {
      await api.post(`/api/journey/plans/${id}/confirm-advance`, {});
      toast.success('Advance released');
      loadPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleConfirmSettlement = async (id) => {
    try {
      await api.post(`/api/journey/settlements/${id}/confirm`, {});
      toast.success('Settlement confirmed');
      loadSettlements();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDownloadSettlementPDF = async (id, empName, planTitle) => {
    try {
      const res = await api.get(`/api/journey/settlements/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Settlement_${empName.replace(/\s/g, '_')}_${planTitle.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
  };

  const handleExportExpenses = async (format) => {
    if (!expFilter.employee_id) { toast.error('Select an employee first'); return; }
    try {
      const params = { employee_id: expFilter.employee_id, format };
      if (expFilter.plan_id) params.plan_id = expFilter.plan_id;
      const res = await api.get('/api/journey/expense-export', { params, responseType: 'blob' });
      // Derive filename from Content-Disposition header, fallback to a sensible default
      let filename = `expenses_${expFilter.employee_id}.${format}`;
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      if (m) filename = m[1];
      const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const handleSubmitExpense = async () => {
    if (!expForm.plan_id) { toast.error('Select a Journey Plan'); return; }
    if (!expForm.expense_date) { toast.error('Select an expense date'); return; }
    const items = expForm.items.filter(i => i.description && i.amount);
    if (items.length === 0) { toast.error('Add at least one expense item'); return; }
    try {
      const { data } = await api.post('/api/journey/expenses', {
        plan_id: expForm.plan_id,
        expense_date: expForm.expense_date,
        items: items.map(i => ({ ...i, amount: parseFloat(i.amount) })),
        remarks: expForm.remarks,
      });
      // Upload attached files
      if (expFiles.length > 0) {
        for (const file of expFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await api.post(`/api/journey/expenses/${data.id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch { /* skip failed uploads silently */ }
        }
        toast.success(`Expense submitted with ${expFiles.length} document(s)`);
      } else {
        toast.success('Expense submitted');
      }
      setExpOpen(false);
      setExpForm({ plan_id: '', expense_date: new Date().toISOString().slice(0, 10), items: [{ description: '', category: 'travel', amount: '' }], remarks: '' });
      setExpFiles([]);
      loadExpenses();
      setActiveExpense(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApproveExpense = async (id, action, amount) => {
    try {
      await api.post(`/api/journey/expenses/${id}/approve`, { action, approved_amount: amount });
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      loadExpenses();
    } catch { toast.error('Failed'); }
  };

  const handleUploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeExpense) return;
    e.target.value = '';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/api/journey/expenses/${activeExpense.id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} uploaded`);
      // Refresh expense
      const { data } = await api.get('/api/journey/expenses');
      setExpenses(data);
      const updated = data.find(ex => ex.id === activeExpense.id);
      if (updated) setActiveExpense(updated);
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleDownloadReceipt = async (expenseId, receipt) => {
    try {
      const r = await api.get(`/api/journey/expenses/${expenseId}/receipt/${receipt.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = receipt.filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleTogglePlanSummary = async (planId) => {
    if (expandedPlan === planId) { setExpandedPlan(null); setPlanSummary(null); return; }
    try {
      const { data } = await api.get(`/api/journey/plans/${planId}/expense-summary`);
      setPlanSummary(data);
      setExpandedPlan(planId);
    } catch { toast.error('Failed to load expenses'); }
  };

  const openReceiptDialog = (expense) => { setActiveExpense(expense); setReceiptOpen(true); };

  const openEditExpense = (expense) => {
    setEditingExpenseId(expense.id);
    setEditExpForm({
      items: (expense.items || []).map(i => ({ ...i, amount: String(i.amount || '') })),
      remarks: expense.remarks || '',
      plan_id: expense.plan_id || '',
    });
    setEditExpOpen(true);
  };

  const handleEditExpense = async () => {
    const items = editExpForm.items.filter(i => i.description && i.amount);
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    try {
      await api.put(`/api/journey/expenses/${editingExpenseId}`, {
        items: items.map(i => ({ ...i, amount: parseFloat(i.amount) })),
        remarks: editExpForm.remarks,
        plan_id: editExpForm.plan_id,
      });
      toast.success('Expense updated');
      setEditExpOpen(false);
      loadExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const addEditExpItem = () => setEditExpForm({ ...editExpForm, items: [...editExpForm.items, { description: '', category: 'travel', amount: '' }] });
  const updateEditExpItem = (i, field, val) => { const items = [...editExpForm.items]; items[i] = { ...items[i], [field]: val }; setEditExpForm({ ...editExpForm, items }); };
  const removeEditExpItem = (i) => setEditExpForm({ ...editExpForm, items: editExpForm.items.filter((_, idx) => idx !== i) });

  const [resubmitMode, setResubmitMode] = useState(false);

  const openResubmitExpense = (expense) => {
    setEditingExpenseId(expense.id);
    setEditExpForm({
      items: (expense.items || []).map(i => ({ ...i, amount: String(i.amount || '') })),
      remarks: expense.remarks || '',
      plan_id: expense.plan_id || '',
    });
    setResubmitMode(true);
    setEditExpOpen(true);
  };

  const handleResubmitExpense = async () => {
    const items = editExpForm.items.filter(i => i.description && i.amount);
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    try {
      await api.post(`/api/journey/expenses/${editingExpenseId}/resubmit`, {
        items: items.map(i => ({ ...i, amount: parseFloat(i.amount) })),
        remarks: editExpForm.remarks,
      });
      toast.success('Expense resubmitted for approval');
      setEditExpOpen(false);
      setResubmitMode(false);
      loadExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resubmit'); }
  };

  const addExpItem = () => setExpForm({ ...expForm, items: [...expForm.items, { description: '', category: 'travel', amount: '' }] });
  const updateExpItem = (i, field, val) => { const items = [...expForm.items]; items[i] = { ...items[i], [field]: val }; setExpForm({ ...expForm, items }); };
  const removeExpItem = (i) => setExpForm({ ...expForm, items: expForm.items.filter((_, idx) => idx !== i) });

  // Visit handlers
  const captureGPS = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setVisitForm(prev => ({ ...prev, location: { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy } }));
        setGpsLoading(false);
        toast.success('Location captured');
      },
      () => { setGpsLoading(false); toast.error('Location access denied'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleCreateVisit = async () => {
    if (!(visitForm.company_name || visitForm.client_name)) { toast.error('Company name is required'); return; }
    try {
      // Map company_name -> client_name for backward compatibility
      const payload = { ...visitForm, client_name: visitForm.company_name || visitForm.client_name };
      await api.post('/api/journey/visits', payload);
      toast.success('Visit recorded');
      setVisitOpen(false);
      setVisitForm({ client_name: '', company_name: '', customer_name: '', contact_number: '', city_town: '', visit_date: '', visit_time: '', purpose: '', remarks: '', plan_id: '', location: {} });
      loadVisits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteVisit = async (id) => {
    if (!window.confirm('Delete this visit?')) return;
    try { await api.delete(`/api/journey/visits/${id}`); toast.success('Deleted'); loadVisits(); } catch { toast.error('Failed'); }
  };

  // Post-visit remarks (salesperson can add remarks after meeting)
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [remarksVisit, setRemarksVisit] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [potentialText, setPotentialText] = useState('');
  const [outcomeText, setOutcomeText] = useState('');
  const [remarksSaving, setRemarksSaving] = useState(false);

  const openEditRemarks = (v) => {
    setRemarksVisit(v);
    setRemarksText(v.meeting_notes || '');
    setPotentialText(v.customer_potential || '');
    setOutcomeText(v.outcome || '');
    setRemarksOpen(true);
  };

  const handleSaveRemarks = async () => {
    if (!remarksVisit) return;
    setRemarksSaving(true);
    try {
      await api.put(`/api/journey/visits/${remarksVisit.id}`, {
        meeting_notes: remarksText,
        customer_potential: potentialText,
        outcome: outcomeText,
      });
      toast.success('Post-meeting details saved');
      setRemarksOpen(false);
      loadVisits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setRemarksSaving(false);
  };

  const pendingPlans = plans.filter(p => p.status === 'pending_approval');
  const totalExpAmt = expenses.reduce((s, e) => s + (e.total_amount || 0), 0);

  // Plans available for expense filing (approved + pending)
  const expensePlans = plans.filter(p => ['pending_approval', 'approved', 'completed'].includes(p.status));
  // My-own plans for Submit Expense dropdown — exclude settled plans
  const settledPlanIds = new Set((settlements || []).filter(s => s.status === 'settled').map(s => s.plan_id));
  const myExpensePlans = plans.filter(p =>
    p.employee_id === user?.employee_id
    && ['pending_approval', 'approved', 'completed'].includes(p.status)
    && !settledPlanIds.has(p.id)
  );

  return (
    <div className="space-y-6" data-testid="journey-plan-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Journey Plans</h1>
          <p className="text-sm text-slate-500 mt-1">Travel plans, field visits & expense management</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setExpOpen(true)} data-testid="submit-expense-btn">
            <IndianRupee className="w-4 h-4 mr-1" /> Submit Expense
          </Button>
          <Button onClick={() => setPlanOpen(true)} className="bg-[#0D2850] hover:bg-[#0B2246]" data-testid="create-plan-btn">
            <Plus className="w-4 h-4 mr-1" /> New Plan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <MapPin className="w-5 h-5 mx-auto text-[#0D2850] mb-1" />
          <div className="text-2xl font-semibold">{plans.length}</div>
          <div className="text-xs text-slate-500">Total Plans</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Clock className="w-5 h-5 mx-auto text-amber-600 mb-1" />
          <div className="text-2xl font-semibold">{pendingPlans.length}</div>
          <div className="text-xs text-slate-500">Pending Approval</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <IndianRupee className="w-5 h-5 mx-auto text-blue-600 mb-1" />
          <div className="text-2xl font-semibold">{expenses.length}</div>
          <div className="text-xs text-slate-500">Expenses Filed</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <IndianRupee className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
          <div className="text-2xl font-semibold">{fmt(totalExpAmt)}</div>
          <div className="text-xs text-slate-500">Total Expenses</div>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100" data-testid="journey-tabs">
          <TabsTrigger value="plans">Journey Plans</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="visits">Visits</TabsTrigger>
          {admin && (
            <TabsTrigger value="exceptions" data-testid="tab-exceptions">
              Exceptions
              {exceptions.filter(e => e.exception_status === 'exception_pending').length > 0 && (
                <Badge className="ml-1.5 bg-red-500 text-white text-[10px] h-4 min-w-[16px] px-1 hover:bg-red-500">{exceptions.filter(e => e.exception_status === 'exception_pending').length}</Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="allowances">Allowances</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements">
            Settlements
            {settlements.filter(s => s.status === 'open').length > 0 && (
              <Badge className="ml-1.5 bg-amber-500 text-white text-[10px] h-4 min-w-[16px] px-1 hover:bg-amber-500">{settlements.filter(s => s.status === 'open').length}</Badge>
            )}
          </TabsTrigger>
          {admin && (
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          )}
          {admin && (
            <TabsTrigger value="allowance_master" data-testid="tab-allowance-master">Allowance Master</TabsTrigger>
          )}
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans">
          <Card>
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3" data-testid="plans-filter-bar">
              {admin && (
                <Select value={planFilter.employee_id} onValueChange={v => setPlanFilter({...planFilter, employee_id: v === 'all' ? '' : v})}>
                  <SelectTrigger className="w-48 h-9 text-sm" data-testid="plan-filter-employee"><SelectValue placeholder="All Employees" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={planFilter.status} onValueChange={v => setPlanFilter({...planFilter, status: v === 'all' ? '' : v})}>
                <SelectTrigger className="w-44 h-9 text-sm" data-testid="plan-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              {(planFilter.employee_id || planFilter.status) && (
                <Button variant="ghost" size="sm" className="text-xs text-slate-500 h-9" onClick={() => setPlanFilter({ employee_id: '', status: '' })} data-testid="plan-filter-clear">Clear Filters</Button>
              )}
            </div>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Employee</TableHead><TableHead>Title</TableHead><TableHead>Dates</TableHead><TableHead>Destinations</TableHead><TableHead className="text-right">Advance</TableHead><TableHead className="text-right">Expenses</TableHead><TableHead>Status</TableHead>{(admin || canApprove || canConfirmAdvance) && <TableHead>Actions</TableHead>}
                </TableRow></TableHeader>
                <TableBody>
                  {plans.map(p => {
                    const expInfo = planExpenseTotals[p.id];
                    const isExpanded = expandedPlan === p.id;
                    return (
                      <React.Fragment key={p.id}>
                        <TableRow className="hover:bg-[#F7FAFF] cursor-pointer" data-testid={`plan-row-${p.id}`} onClick={() => handleTogglePlanSummary(p.id)}>
                          <TableCell className="w-8 pr-0">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          </TableCell>
                          <TableCell className="text-sm font-medium">{p.employee_name}</TableCell>
                          <TableCell className="text-sm">{p.title}<div className="text-xs text-slate-500">{p.purpose}</div></TableCell>
                          <TableCell className="text-sm text-slate-600">{p.start_date} to {p.end_date}</TableCell>
                          <TableCell className="text-sm">{(p.destinations || []).join(', ')}</TableCell>
                          <TableCell className="text-right text-sm">
                            {p.advance_amount > 0 ? (
                              <div>
                                <span className="font-semibold">{fmt(p.advance_amount)}</span>
                                <div className="mt-0.5">
                                  {p.advance_status === 'advance_released' ? (
                                    <Badge className="text-[9px] bg-teal-100 text-teal-800">Released</Badge>
                                  ) : p.advance_status === 'pending_advance' ? (
                                    <Badge className="text-[9px] bg-orange-100 text-orange-800">Pending</Badge>
                                  ) : null}
                                </div>
                              </div>
                            ) : <span className="text-slate-400 text-xs">-</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {expInfo ? (
                              <div>
                                <span className="font-semibold">{fmt(expInfo.total)}</span>
                                <div className="text-[10px] text-slate-400">{expInfo.count} expense{expInfo.count > 1 ? 's' : ''}</div>
                              </div>
                            ) : <span className="text-slate-400 text-xs">-</span>}
                          </TableCell>
                          <TableCell><Badge className={`text-xs ${STATUS_COLORS[p.status] || ''}`}>{p.status?.replace('_', ' ')}</Badge></TableCell>
                          {(admin || canApprove || canConfirmAdvance) && (
                            <TableCell onClick={e => e.stopPropagation()}>
                              <div className="flex gap-1 flex-wrap">
                                {p.status === 'pending_approval' && (admin || canApprove) && (
                                  <>
                                    <Button size="sm" variant="ghost" className="text-emerald-600 h-7" onClick={() => handleApprovePlan(p.id, 'approve')} data-testid={`approve-plan-${p.id}`}><Check className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => handleApprovePlan(p.id, 'reject')} data-testid={`reject-plan-${p.id}`}><X className="w-4 h-4" /></Button>
                                  </>
                                )}
                                {p.status === 'approved' && p.advance_status === 'pending_advance' && canConfirmAdvance && (
                                  <Button size="sm" className="h-7 bg-teal-600 hover:bg-teal-700 text-white text-xs" onClick={() => handleConfirmAdvance(p.id)} data-testid={`confirm-advance-${p.id}`}>
                                    Release Advance
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                        {/* Expanded expense summary */}
                        {isExpanded && planSummary && (
                          <TableRow>
                            <TableCell colSpan={(admin || canApprove || canConfirmAdvance) ? 9 : 8} className="bg-slate-50 p-0">
                              <div className="p-4 space-y-3" data-testid={`plan-summary-${p.id}`}>
                                <div className="flex items-center gap-4 flex-wrap">
                                  <div className="text-sm"><span className="text-slate-500">Total:</span> <span className="font-bold text-[#0B1B33]">{fmt(planSummary.total_amount)}</span></div>
                                  <div className="text-sm"><span className="text-slate-500">Approved:</span> <span className="font-bold text-emerald-700">{fmt(planSummary.approved_amount)}</span></div>
                                  <div className="text-sm"><span className="text-slate-500">Count:</span> {planSummary.expense_count}</div>
                                  {Object.entries(planSummary.by_category || {}).map(([cat, amt]) => (
                                    <Badge key={cat} variant="outline" className="text-[10px]">{cat}: {fmt(amt)}</Badge>
                                  ))}
                                </div>
                                {planSummary.expenses?.length > 0 && (
                                  <Table>
                                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Receipts</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                      {planSummary.expenses.map(ex => (
                                        <TableRow key={ex.id}>
                                          <TableCell className="text-xs">{ex.expense_date}</TableCell>
                                          <TableCell className="text-xs">{(ex.items || []).map(i => i.description).join(', ')}</TableCell>
                                          <TableCell className="text-right text-xs font-semibold">{fmt(ex.total_amount)}</TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-1">
                                              <Paperclip className="w-3 h-3 text-slate-400" />
                                              <span className="text-xs">{(ex.receipts || []).length}</span>
                                            </div>
                                          </TableCell>
                                          <TableCell><Badge className={`text-[10px] ${STATUS_COLORS[ex.status]}`}>{ex.status}</Badge></TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {plans.length === 0 && <TableRow><TableCell colSpan={admin ? 8 : 7} className="text-center text-sm text-slate-500 py-8">No journey plans yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3" data-testid="expenses-filter-bar">
              {(() => {
                const canExport = admin || canApprove || canConfirmAdvance;
                // If a privileged user has picked an employee, the plan list narrows to only that employee's plans
                const planOptions = canExport && expFilter.employee_id
                  ? plans.filter(p => p.employee_id === expFilter.employee_id)
                  : plans;
                return (
                  <>
                    {canExport && (
                      <Select
                        value={expFilter.employee_id}
                        onValueChange={v => setExpFilter({ ...expFilter, employee_id: v === 'all' ? '' : v, plan_id: '' })}
                      >
                        <SelectTrigger className="w-52 h-9 text-sm" data-testid="exp-filter-employee">
                          <SelectValue placeholder="All Employees" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Employees</SelectItem>
                          {(() => {
                            // Build employee options from plans available to the user (respects backend scope)
                            const seen = new Map();
                            plans.forEach(p => { if (p.employee_id && !seen.has(p.employee_id)) seen.set(p.employee_id, p.employee_name || p.employee_id); });
                            // Admin/HR also get the full employees list merged in
                            if (admin) employees.forEach(e => { if (!seen.has(e.id)) seen.set(e.id, e.name); });
                            return Array.from(seen.entries()).map(([id, name]) => (
                              <SelectItem key={id} value={id}>{name}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    )}
                    <Select value={expFilter.plan_id} onValueChange={v => setExpFilter({ ...expFilter, plan_id: v === 'all' ? '' : v })}>
                      <SelectTrigger className="w-52 h-9 text-sm" data-testid="exp-filter-plan"><SelectValue placeholder="All Plans" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Plans</SelectItem>
                        {planOptions.map(p => <SelectItem key={p.id} value={p.id}>{p.title} ({p.start_date})</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={expFilter.status} onValueChange={v => setExpFilter({ ...expFilter, status: v === 'all' ? '' : v })}>
                      <SelectTrigger className="w-40 h-9 text-sm" data-testid="exp-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    {(expFilter.plan_id || expFilter.employee_id || expFilter.status) && (
                      <Button variant="ghost" size="sm" className="text-xs text-slate-500 h-9" onClick={() => setExpFilter({ plan_id: '', employee_id: '', status: '' })} data-testid="exp-filter-clear">Clear Filters</Button>
                    )}
                    {canExport && expFilter.employee_id && (
                      <div className="ml-auto flex items-center gap-2" data-testid="expense-export-bar">
                        <span className="text-[11px] text-slate-500">Export:</span>
                        <Button
                          size="sm" variant="outline" className="h-9 gap-1 text-xs"
                          onClick={() => handleExportExpenses('csv')}
                          data-testid="export-expense-csv-btn"
                        >
                          <Download className="w-3.5 h-3.5" />CSV
                        </Button>
                        <Button
                          size="sm" variant="outline" className="h-9 gap-1 text-xs"
                          onClick={() => handleExportExpenses('pdf')}
                          data-testid="export-expense-pdf-btn"
                        >
                          <Download className="w-3.5 h-3.5" />PDF
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Employee</TableHead><TableHead>Journey Plan</TableHead><TableHead>Date</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Receipts</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {expenses.map(e => (
                    <TableRow key={e.id} className="hover:bg-[#F7FAFF]" data-testid={`expense-row-${e.id}`}>
                      <TableCell className="text-sm font-medium">{e.employee_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{e.plan_title || <span className="text-slate-400">-</span>}</TableCell>
                      <TableCell className="text-sm text-slate-600">{e.expense_date}</TableCell>
                      <TableCell className="text-sm">{(e.items || []).map(i => i.description).join(', ')}</TableCell>
                      <TableCell className="text-right text-sm font-semibold">{fmt(e.total_amount)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => openReceiptDialog(e)} data-testid={`receipts-btn-${e.id}`}>
                          <Paperclip className="w-3 h-3" /> {(e.receipts || []).length}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge className={`text-xs ${STATUS_COLORS[e.status] || ''}`}>{e.status}{e.resubmit_count > 0 ? ` (x${e.resubmit_count})` : ''}</Badge>
                          {e.exceeds_limit && <Badge className="text-[10px] bg-red-100 text-red-600 hover:bg-red-100">Over Limit</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {e.status === 'pending' ? (
                            <Button size="sm" variant="ghost" className="h-7 text-[#0D2850]" onClick={() => openEditExpense(e)} data-testid={`edit-exp-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          ) : e.status === 'rejected' ? (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 text-blue-600 gap-1" onClick={() => openResubmitExpense(e)} data-testid={`resubmit-exp-${e.id}`}>
                                <RotateCcw className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          ) : (
                            <Lock className="w-3.5 h-3.5 text-slate-300 mt-1.5" title="Locked after approval" />
                          )}
                          {admin && e.status === 'pending' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-emerald-600 h-7" onClick={() => handleApproveExpense(e.id, 'approve', e.total_amount)} data-testid={`approve-exp-${e.id}`}><Check className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => handleApproveExpense(e.id, 'reject', 0)} data-testid={`reject-exp-${e.id}`}><X className="w-4 h-4" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {expenses.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">No expenses filed</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visits Tab */}
        <TabsContent value="visits">
          {/* Visit Analytics Section (Admin/HR only) */}
          {admin && visitAnalytics && (
            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4 text-center">
                  <Navigation className="w-5 h-5 mx-auto text-[#0D2850] mb-1" />
                  <div className="text-2xl font-semibold">{visitAnalytics.summary.total_visits}</div>
                  <div className="text-xs text-slate-500">Total Visits</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <Building2 className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                  <div className="text-2xl font-semibold">{visitAnalytics.summary.unique_clients}</div>
                  <div className="text-xs text-slate-500">Unique Clients</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <Users className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                  <div className="text-2xl font-semibold">{visitAnalytics.summary.active_employees}</div>
                  <div className="text-xs text-slate-500">Active Reps</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <BarChart3 className="w-5 h-5 mx-auto text-violet-600 mb-1" />
                  <div className="text-2xl font-semibold">{visitAnalytics.summary.avg_visits_per_employee}</div>
                  <div className="text-xs text-slate-500">Avg Visits/Rep</div>
                </CardContent></Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Visits by Employee */}
                {visitAnalytics.by_employee?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Visits by Employee</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="visit-employee-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={visitAnalytics.by_employee.slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis dataKey="employee_name" type="category" width={110} tick={{ fontSize: 10 }} />
                            <Tooltip {...TIP_STYLE} />
                            <Bar dataKey="visits" name="Visits" fill="#0D2850" radius={[0,4,4,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top Clients */}
                {visitAnalytics.by_client?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Top Clients Visited</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[260px]" data-testid="visit-client-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={visitAnalytics.by_client.slice(0, 10)} layout="vertical" margin={{ left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis dataKey="client" type="category" width={110} tick={{ fontSize: 10 }} />
                            <Tooltip {...TIP_STYLE} />
                            <Bar dataKey="visits" name="Visits" fill="#059669" radius={[0,4,4,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Monthly Trend */}
                {visitAnalytics.monthly_trend?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Monthly Visit Trend</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[240px]" data-testid="visit-monthly-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={visitAnalytics.monthly_trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip {...TIP_STYLE} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="visits" name="Visits" fill="#0D2850" radius={[4,4,0,0]} />
                            <Bar dataKey="unique_clients" name="Unique Clients" fill="#7c3aed" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Day of Week Distribution */}
                {visitAnalytics.by_weekday?.some(d => d.visits > 0) && (
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Visits by Day of Week</CardTitle></CardHeader>
                    <CardContent className="p-3">
                      <div className="h-[240px]" data-testid="visit-weekday-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={visitAnalytics.by_weekday}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip {...TIP_STYLE} />
                            <Bar dataKey="visits" name="Visits" fill="#b45309" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Employee Performance Table */}
              {visitAnalytics.by_employee?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="py-3 px-5 pb-1">
                    <CardTitle className="text-sm font-semibold">Employee Summary (Visits + Expenses)</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50">
                        <TableHead>#</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead className="text-center">Plans</TableHead>
                        <TableHead className="text-center">Visits</TableHead>
                        <TableHead className="text-center">Unique Clients</TableHead>
                        <TableHead className="text-right">Total Expense (₹)</TableHead>
                        <TableHead className="text-right">Approved (₹)</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {visitAnalytics.by_employee.map((e, i) => (
                          <TableRow key={e.employee_id} data-testid={`emp-summary-row-${e.employee_id}`}>
                            <TableCell className="text-sm text-slate-400">{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium">{e.employee_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{e.department}</TableCell>
                            <TableCell className="text-center text-sm">{e.plans || 0}</TableCell>
                            <TableCell className="text-center text-sm font-semibold">{e.visits}</TableCell>
                            <TableCell className="text-center text-sm">{e.unique_clients}</TableCell>
                            <TableCell className="text-right text-sm font-tabular font-semibold text-[#0D2850]">
                              {e.total_expenses ? fmt(e.total_expenses) : '—'}
                            </TableCell>
                            <TableCell className="text-right text-sm font-tabular text-emerald-600">
                              {e.approved_expenses ? fmt(e.approved_expenses) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Visit Log Table */}
          <Card>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Navigation className="w-4 h-4" /> Client Visits</h3>
              <Button size="sm" onClick={() => { setVisitOpen(true); captureGPS(); }} className="bg-[#0D2850] hover:bg-[#0B2246]" data-testid="log-visit-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Log Visit
              </Button>
            </div>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Employee</TableHead><TableHead>Company</TableHead><TableHead>Customer</TableHead><TableHead>City/Town</TableHead><TableHead>Contact</TableHead><TableHead>Tour Date</TableHead><TableHead>Location</TableHead><TableHead>Purpose</TableHead><TableHead>Remarks</TableHead><TableHead>Post-Meeting</TableHead><TableHead>Potential</TableHead><TableHead>Outcome</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {visits.map(v => (
                    <TableRow key={v.id} className="hover:bg-[#F7FAFF]" data-testid={`visit-row-${v.id}`}>
                      <TableCell className="text-sm font-medium">{v.employee_name}</TableCell>
                      <TableCell className="text-sm">{v.company_name || v.client_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{v.customer_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{v.city_town || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-600">{v.contact_number || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{v.visit_date} <span className="text-slate-400">{v.visit_time}</span></TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {v.location?.lat ? (
                          <a href={`https://maps.google.com/?q=${v.location.lat},${v.location.lng}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1" data-testid={`visit-map-${v.id}`}>
                            <MapPin className="w-3 h-3" /> Map
                          </a>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-sm max-w-[160px] truncate" title={v.purpose}>{v.purpose || '-'}</TableCell>
                      <TableCell className="text-sm max-w-[160px]">
                        <span className="text-slate-600 line-clamp-2" title={v.remarks}>{v.remarks || <span className="italic text-slate-400">-</span>}</span>
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px]">
                        <div className="flex items-start gap-1.5">
                          <span className="text-slate-700 line-clamp-2 flex-1" title={v.meeting_notes} data-testid={`visit-meeting-notes-${v.id}`}>
                            {v.meeting_notes || <span className="italic text-slate-400">Add notes</span>}
                          </span>
                          {(admin || v.employee_id === user?.employee_id) && (
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0 text-slate-500 hover:text-[#0D2850]" onClick={() => openEditRemarks(v)} data-testid={`edit-remarks-${v.id}`} title="Add / edit post-meeting details">
                              <Pencil className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[120px] truncate" title={v.customer_potential}>{v.customer_potential || '-'}</TableCell>
                      <TableCell className="text-sm max-w-[160px]">
                        <span className="text-slate-600 line-clamp-2" title={v.outcome}>{v.outcome || <span className="italic text-slate-400">-</span>}</span>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteVisit(v.id)} data-testid={`delete-visit-${v.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visits.length === 0 && <TableRow><TableCell colSpan={13} className="text-center text-sm text-slate-500 py-8">No visits recorded yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Office vs Field Calendar</CardTitle>
                <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="calendar-month-input" />
              </div>
            </CardHeader>
            <CardContent>
              <CalendarGrid month={month} fieldDays={calData.field_days} officeDays={calData.office_days} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allowances Tab */}
        <TabsContent value="allowances">
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Travel Allowance Policy</CardTitle></CardHeader>
            <CardContent>
              {!admin && allowances.my_allowance ? (
                <div data-testid="my-allowance-card">
                  <div className="bg-[#0D2850]/5 border border-[#0D2850]/10 rounded-lg p-5 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-[#0D2850] text-white text-xs hover:bg-[#0D2850]">{allowances.my_category || 'Default'}</Badge>
                      {allowances.my_designation && <span className="text-xs text-slate-500">({allowances.my_designation})</span>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[10px] uppercase text-slate-400 mb-1">Daily DA</p>
                        <p className="text-lg font-semibold text-[#0B1B33]">{fmt(allowances.my_allowance.daily_da)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-400 mb-1">Hotel Limit</p>
                        <p className="text-lg font-semibold text-[#0B1B33]">{fmt(allowances.my_allowance.hotel_limit)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-400 mb-1">Travel Mode</p>
                        <p className="text-lg font-semibold text-[#0B1B33]">{allowances.my_allowance.travel_mode}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-400 mb-1">Local Conv.</p>
                        <p className="text-lg font-semibold text-[#0B1B33]">{fmt(allowances.my_allowance.local_conveyance)}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400">These are your applicable travel allowance limits based on your designation. Contact HR for any queries.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Designation</TableHead><TableHead className="text-right">Daily DA</TableHead><TableHead className="text-right">Hotel Limit</TableHead><TableHead>Travel Mode</TableHead><TableHead className="text-right">Local Conv.</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {Object.entries(allowances.all || {}).map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell className="text-sm font-medium">{k}</TableCell>
                        <TableCell className="text-right text-sm">{v.daily_da?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-right text-sm">{v.hotel_limit?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-sm">{v.travel_mode}</TableCell>
                        <TableCell className="text-right text-sm">{v.local_conveyance?.toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exceptions Tab */}
        <TabsContent value="exceptions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-500" />
                  <CardTitle className="text-sm font-semibold">Over-Limit Expense Exceptions</CardTitle>
                </div>
                <Badge className="bg-slate-100 text-slate-600 text-xs hover:bg-slate-100">{exceptions.length} total</Badge>
              </div>
              <p className="text-xs text-slate-400 mt-1">Expenses exceeding allowance limits require special approval from the Director (Rishabh Singhania)</p>
            </CardHeader>
            <CardContent>
              {exceptions.length === 0 ? (
                <div className="text-center py-10">
                  <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No over-limit exceptions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {exceptions.map(exc => {
                    const isPending = exc.exception_status === 'exception_pending';
                    const isApproved = exc.exception_status === 'exception_approved';
                    const isRejected = exc.exception_status === 'exception_rejected';
                    return (
                      <div key={exc.id} className={`border rounded-lg p-4 ${isPending ? 'border-amber-200 bg-amber-50/50' : isApproved ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`} data-testid={`exception-${exc.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-sm font-semibold text-[#0B1B33]">{exc.employee_name}</span>
                              <span className="text-xs text-slate-400">{exc.department}</span>
                              <Badge className={isPending ? 'bg-amber-100 text-amber-700' : isApproved ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} variant="outline">
                                {isPending ? 'Pending Approval' : isApproved ? 'Approved' : 'Rejected'}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500">{exc.plan_title} &bull; {exc.expense_date} &bull; Total: <span className="font-semibold text-[#0B1B33]">{fmt(exc.total_amount)}</span></p>
                            {/* Breach details */}
                            <div className="mt-2 space-y-1">
                              {(exc.limit_breaches || []).map((b, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                                  <span className="text-slate-600">
                                    <span className="font-medium capitalize">{b.category}</span>
                                    {b.description ? ` (${b.description})` : ''}: {fmt(b.amount)} &gt; limit {fmt(b.limit)}
                                    <span className="text-red-500 font-medium ml-1">+{fmt(b.excess)} over</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                            {exc.remarks && <p className="text-xs text-slate-400 mt-1 italic">Remarks: {exc.remarks}</p>}
                            {exc.exception_approved_by && <p className="text-xs text-slate-500 mt-1">Actioned by: {exc.exception_approved_by}</p>}
                          </div>
                          {/* Approval buttons - only for Rishabh/Admin */}
                          {canApproveExceptions && isPending && (
                            <div className="flex gap-1.5 shrink-0">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs gap-1" data-testid={`approve-exception-${exc.id}`}
                                onClick={async () => {
                                  try {
                                    await api.post(`/api/journey/expenses/${exc.id}/approve`, { action: 'approve', approved_amount: exc.total_amount, remarks: 'Exception approved by Director' });
                                    toast.success('Exception approved');
                                    loadExceptions(); loadExpenses();
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                                }}>
                                <Check className="w-3 h-3" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs gap-1" data-testid={`reject-exception-${exc.id}`}
                                onClick={async () => {
                                  const reason = window.prompt('Reason for rejection:');
                                  if (reason === null) return;
                                  try {
                                    await api.post(`/api/journey/expenses/${exc.id}/approve`, { action: 'reject', remarks: reason || 'Exception rejected by Director' });
                                    toast.success('Exception rejected');
                                    loadExceptions(); loadExpenses();
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                                }}>
                                <X className="w-3 h-3" /> Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settlements Tab */}
        <TabsContent value="settlements" data-testid="settlements-tab">
          {/* Settlement Summary Cards */}
          {settlements.length > 0 && (() => {
            const openSettlements = settlements.filter(s => s.status === 'open');
            const pendingCount = openSettlements.length;
            const toRecover = openSettlements.filter(s => s.balance > 0).reduce((sum, s) => sum + s.balance, 0);
            const toReimburse = openSettlements.filter(s => s.balance < 0).reduce((sum, s) => sum + Math.abs(s.balance), 0);
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="settlement-summary-cards">
                <Card className="border-amber-200 bg-amber-50/40">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-amber-700" data-testid="pending-settlements-count">{pendingCount}</div>
                      <div className="text-xs text-amber-600/80">Pending Settlements</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/40">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                      <IndianRupee className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-700" data-testid="amount-to-recover">{fmt(toRecover)}</div>
                      <div className="text-xs text-red-600/80">To Recover (Emp Owes)</div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50/40">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                      <IndianRupee className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-700" data-testid="reimbursements-due">{fmt(toReimburse)}</div>
                      <div className="text-xs text-emerald-600/80">Reimbursements Due</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-[#0D2850]">Tour Settlements</h3>
                  <p className="text-xs text-slate-500">Auto-generated when trip ends and expenses are filed. Advance vs actual expense reconciliation.</p>
                </div>
                <Badge variant="outline" className="text-xs">{settlements.length} total</Badge>
              </div>
              {settlements.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <IndianRupee className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No settlements yet</p>
                  <p className="text-xs mt-1">Settlements are auto-generated when approved plans end and expenses are filed.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="text-right">Advance</TableHead>
                      <TableHead className="text-right">Expenses</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {settlements.map(s => {
                        const bal = s.balance || 0;
                        const balColor = bal > 0 ? 'text-red-600' : bal < 0 ? 'text-emerald-600' : 'text-slate-600';
                        const balLabel = bal > 0 ? 'Emp owes' : bal < 0 ? 'Co. owes' : 'Even';
                        return (
                          <TableRow key={s.id} className="hover:bg-[#F7FAFF]" data-testid={`settlement-row-${s.id}`}>
                            <TableCell className="text-sm font-medium">{s.employee_name}<div className="text-[10px] text-slate-400">{s.department}</div></TableCell>
                            <TableCell className="text-sm">{s.plan_title}</TableCell>
                            <TableCell className="text-xs text-slate-600">{s.start_date} to {s.end_date}</TableCell>
                            <TableCell className="text-right text-sm">{s.advance_amount > 0 ? fmt(s.advance_amount) : '-'}</TableCell>
                            <TableCell className="text-right text-sm">
                              <div>{fmt(s.approved_expenses)}</div>
                              <div className="text-[10px] text-slate-400">{s.approved_count} approved{s.pending_count > 0 ? `, ${s.pending_count} pending` : ''}</div>
                            </TableCell>
                            <TableCell className={`text-right text-sm font-semibold ${balColor}`}>
                              <div>{fmt(Math.abs(bal))}</div>
                              <div className="text-[10px] font-normal">{balLabel}</div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-xs ${s.status === 'settled' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                {s.status === 'settled' ? 'Settled' : 'Open'}
                              </Badge>
                              {s.confirmed_by && <div className="text-[10px] text-slate-400 mt-0.5">by {s.confirmed_by}</div>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {s.status === 'open' && canConfirmAdvance && (
                                  <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => handleConfirmSettlement(s.id)} data-testid={`confirm-settlement-${s.id}`}>
                                    Settle
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleDownloadSettlementPDF(s.id, s.employee_name, s.plan_title)} data-testid={`download-settlement-${s.id}`}>
                                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab (Admin/HR only) */}
        {admin && (
          <TabsContent value="reports" data-testid="reports-tab">
            <JourneyReportsTab employees={employees} />
          </TabsContent>
        )}

        {/* Allowance Master Tab (Admin/HR only) */}
        {admin && (
          <TabsContent value="allowance_master" data-testid="allowance-master-tab-content">
            <AllowanceMasterTab />
          </TabsContent>
        )}
      </Tabs>

      {/* New Plan Dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-slate-100"><DialogTitle>Create Journey Plan</DialogTitle></DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1">
            {admin && (
              <div><Label>Employee</Label>
                <Select value={planForm.employee_id} onValueChange={v => setPlanForm({...planForm, employee_id: v})}>
                  <SelectTrigger data-testid="plan-employee-select"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} - {e.department}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Title</Label><Input value={planForm.title} onChange={e => setPlanForm({...planForm, title: e.target.value})} placeholder="e.g. Odisha Market Visit" data-testid="plan-title-input" /></div>
            <div><Label>Purpose</Label><Textarea value={planForm.purpose} onChange={e => setPlanForm({...planForm, purpose: e.target.value})} rows={2} placeholder="Purpose of travel" data-testid="plan-purpose-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tour Date</Label><Input type="date" value={planForm.start_date} onChange={e => setPlanForm({...planForm, start_date: e.target.value})} data-testid="plan-start-date" /></div>
              <div><Label>End Date</Label><Input type="date" value={planForm.end_date} onChange={e => setPlanForm({...planForm, end_date: e.target.value})} data-testid="plan-end-date" /></div>
            </div>
            <div><Label>City / Town of Visit (comma separated)</Label><Input value={planForm.destinations} onChange={e => setPlanForm({...planForm, destinations: e.target.value})} placeholder="e.g. Bhubaneswar, Cuttack" data-testid="plan-destinations-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer Name</Label><Input value={planForm.customer_name} onChange={e => setPlanForm({...planForm, customer_name: e.target.value})} placeholder="e.g. Ramesh Kumar" data-testid="plan-customer-name" /></div>
              <div><Label>Company Name</Label><Input value={planForm.company_name} onChange={e => setPlanForm({...planForm, company_name: e.target.value})} placeholder="e.g. ABC Industries" data-testid="plan-company-name" /></div>
            </div>
            <div><Label>Contact No. of Person Visited</Label><Input value={planForm.contact_number} onChange={e => setPlanForm({...planForm, contact_number: e.target.value})} placeholder="e.g. +91 98765 43210" data-testid="plan-contact-input" /></div>
            <div><Label>Remarks</Label><Textarea value={planForm.remarks} onChange={e => setPlanForm({...planForm, remarks: e.target.value})} rows={2} placeholder="Any pre-visit notes" data-testid="plan-remarks-input" /></div>
            <div><Label>Tour Advance Amount (optional)</Label><Input type="number" min="0" step="500" value={planForm.advance_amount} onChange={e => setPlanForm({...planForm, advance_amount: e.target.value})} placeholder="e.g. 5000" data-testid="plan-advance-input" /></div>
          </div>
          <div className="px-6 py-3 border-t border-slate-100 shrink-0 bg-white">
            <Button onClick={handleCreatePlan} className="w-full bg-[#0D2850]" data-testid="plan-submit-btn">Create Plan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={expOpen} onOpenChange={(v) => { setExpOpen(v); if (!v) setExpFiles([]); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="submit-expense-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-slate-100"><DialogTitle>Submit Expense</DialogTitle></DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1">
            <div><Label>Journey Plan <span className="text-red-500">*</span></Label>
              <Select value={expForm.plan_id} onValueChange={v => setExpForm({...expForm, plan_id: v})}>
                <SelectTrigger data-testid="exp-plan-select"><SelectValue placeholder="Select Journey Plan" /></SelectTrigger>
                <SelectContent>
                  {myExpensePlans.length === 0 ? (
                    <SelectItem value="__none__" disabled>No eligible plans (approved, not settled)</SelectItem>
                  ) : myExpensePlans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title} ({p.start_date}) — {p.status.replace('_',' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {myExpensePlans.length === 0 && (
                <p className="text-[11px] text-slate-400 mt-1">You don&apos;t have any open plans to file expenses against. Create a plan first, or wait for an existing plan to be approved.</p>
              )}
            </div>
            <div><Label>Expense Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={expForm.expense_date} max={new Date().toISOString().slice(0, 10)} onChange={e => setExpForm({...expForm, expense_date: e.target.value})} data-testid="exp-date-input" />
              <p className="text-[11px] text-slate-400 mt-1">Date the expense was incurred — this shows in the expense report.</p>
            </div>
            <div className="space-y-2">
              <Label>Expense Items</Label>
              {expForm.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={item.description} onChange={e => updateExpItem(i, 'description', e.target.value)} placeholder="Description" className="flex-1" data-testid={`exp-item-desc-${i}`} />
                  <Select value={item.category} onValueChange={v => updateExpItem(i, 'category', v)}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="travel">Travel</SelectItem>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="food">Food/DA</SelectItem>
                      <SelectItem value="local">Local Conv.</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={item.amount} onChange={e => updateExpItem(i, 'amount', e.target.value)} placeholder="Amt" className="w-24" data-testid={`exp-item-amt-${i}`} />
                  {expForm.items.length > 1 && <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => removeExpItem(i)}><X className="w-4 h-4" /></Button>}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addExpItem} data-testid="add-exp-item-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
            </div>
            <div>
              <Label>Supporting Documents</Label>
              <input type="file" ref={expFileRef} className="hidden" multiple onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setExpFiles(prev => [...prev, ...files]);
                e.target.value = '';
              }} data-testid="exp-file-input" />
              <div
                className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-[#0D2850]/30 transition-colors mt-1"
                onClick={() => expFileRef.current?.click()}
              >
                <Upload className="w-5 h-5 text-slate-300 mx-auto mb-1" />
                <p className="text-xs text-slate-400">Click to attach receipts, bills, or supporting documents</p>
              </div>
              {expFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {expFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md text-xs" data-testid={`exp-attached-file-${i}`}>
                      <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="flex-1 truncate text-slate-600">{f.name}</span>
                      <span className="text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                      <button className="text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); setExpFiles(prev => prev.filter((_, idx) => idx !== i)); }}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div><Label>Remarks</Label><Textarea value={expForm.remarks} onChange={e => setExpForm({...expForm, remarks: e.target.value})} rows={2} data-testid="exp-remarks-input" /></div>
            {/* Limit breach warning */}
            {(() => {
              const limitMap = { hotel: 'hotel_limit', food: 'daily_da', local: 'local_conveyance' };
              const myAllow = allowances.my_allowance || allowances.all?.default || {};
              const breaches = expForm.items.filter(i => {
                const key = limitMap[i.category];
                return key && parseFloat(i.amount || 0) > (myAllow[key] || 0);
              });
              if (breaches.length === 0) return null;
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="limit-breach-warning">
                  <div className="flex items-center gap-1.5 text-amber-700 text-xs font-semibold">
                    <AlertTriangle className="w-3.5 h-3.5" /> Over-Limit Warning
                  </div>
                  {breaches.map((b, i) => {
                    const key = limitMap[b.category];
                    const limit = myAllow[key] || 0;
                    return (
                      <p key={i} className="text-[11px] text-amber-600">
                        {b.description || b.category}: {fmt(parseFloat(b.amount || 0))} exceeds limit of {fmt(limit)} (+{fmt(parseFloat(b.amount || 0) - limit)})
                      </p>
                    );
                  })}
                  <p className="text-[10px] text-amber-500 mt-1">This expense will require special approval from the Director.</p>
                </div>
              );
            })()}
          </div>
          <div className="px-6 py-3 border-t border-slate-100 shrink-0 bg-white">
            <Button onClick={handleSubmitExpense} className="w-full bg-[#0D2850]" data-testid="exp-submit-btn" disabled={myExpensePlans.length === 0}>Submit Expense</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipts Dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Receipts & Bills</DialogTitle></DialogHeader>
          <input type="file" ref={receiptRef} className="hidden" onChange={handleUploadReceipt} data-testid="receipt-file-input" />
          {activeExpense && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-slate-600">
                <span className="font-medium">{activeExpense.plan_title}</span> &bull; {activeExpense.expense_date} &bull; {fmt(activeExpense.total_amount)}
              </div>
              {(activeExpense.receipts || []).length > 0 ? (
                <div className="space-y-2">
                  {activeExpense.receipts.map(r => (
                    <div key={r.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg" data-testid={`receipt-${r.id}`}>
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{r.filename}</p>
                        <p className="text-[10px] text-slate-400">{r.uploaded_at?.split('T')[0]}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadReceipt(activeExpense.id, r)} data-testid={`download-receipt-${r.id}`}>
                        <Download className="w-3.5 h-3.5 text-[#0D2850]" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No receipts uploaded yet</p>
              )}
              {activeExpense.status === 'pending' && (
                <Button variant="outline" className="w-full gap-1" onClick={() => receiptRef.current?.click()} disabled={uploading} data-testid="upload-receipt-btn">
                  <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Receipt / Bill'}
                </Button>
              )}
              {activeExpense.status !== 'pending' && (activeExpense.receipts || []).length > 0 && (
                <p className="text-xs text-slate-400 text-center">Expense is locked — receipts are view-only</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit / Resubmit Expense Dialog */}
      <Dialog open={editExpOpen} onOpenChange={(v) => { setEditExpOpen(v); if (!v) setResubmitMode(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" data-testid="edit-expense-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-slate-100"><DialogTitle>{resubmitMode ? 'Resubmit Expense' : 'Edit Expense'}</DialogTitle></DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1">
            {resubmitMode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                This expense was rejected. Update the details and resubmit for approval.
              </div>
            )}
            <div><Label>Journey Plan <span className="text-red-500">*</span></Label>
              <Select value={editExpForm.plan_id} onValueChange={v => setEditExpForm({...editExpForm, plan_id: v})} disabled={resubmitMode}>
                <SelectTrigger data-testid="edit-exp-plan-select"><SelectValue placeholder="Select Journey Plan" /></SelectTrigger>
                <SelectContent>
                  {myExpensePlans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title} ({p.start_date})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Expense Items</Label>
              {editExpForm.items.map((item, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={item.description} onChange={e => updateEditExpItem(i, 'description', e.target.value)} placeholder="Description" className="flex-1" data-testid={`edit-exp-item-desc-${i}`} />
                  <Select value={item.category} onValueChange={v => updateEditExpItem(i, 'category', v)}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="travel">Travel</SelectItem>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="food">Food/DA</SelectItem>
                      <SelectItem value="local">Local Conv.</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={item.amount} onChange={e => updateEditExpItem(i, 'amount', e.target.value)} placeholder="Amt" className="w-24" data-testid={`edit-exp-item-amt-${i}`} />
                  {editExpForm.items.length > 1 && <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => removeEditExpItem(i)}><X className="w-4 h-4" /></Button>}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addEditExpItem} data-testid="edit-add-exp-item-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
            </div>
            <div><Label>Remarks</Label><Textarea value={editExpForm.remarks} onChange={e => setEditExpForm({...editExpForm, remarks: e.target.value})} rows={2} data-testid="edit-exp-remarks-input" /></div>
          </div>
          <div className="px-6 py-3 border-t border-slate-100 shrink-0 bg-white">
            {resubmitMode ? (
              <Button onClick={handleResubmitExpense} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="resubmit-exp-submit-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Resubmit for Approval
              </Button>
            ) : (
              <Button onClick={handleEditExpense} className="w-full bg-[#0D2850]" data-testid="edit-exp-submit-btn">Save Changes</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Remarks Dialog (post-visit remarks) */}
      <Dialog open={remarksOpen} onOpenChange={setRemarksOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0" data-testid="edit-remarks-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-slate-100">
            <DialogTitle>Post-Meeting Details</DialogTitle>
          </DialogHeader>
          {remarksVisit && (
            <div className="space-y-3 overflow-y-auto px-6 py-4">
              <div className="bg-slate-50 rounded-md p-2.5 text-xs">
                <div className="font-medium text-slate-700">{remarksVisit.company_name || remarksVisit.client_name}</div>
                <div className="text-slate-500">{remarksVisit.visit_date} · {remarksVisit.visit_time} · {remarksVisit.employee_name}</div>
                {remarksVisit.customer_name && <div className="text-slate-500 mt-0.5"><span className="font-medium">Customer:</span> {remarksVisit.customer_name}</div>}
                {remarksVisit.purpose && <div className="text-slate-500 mt-0.5"><span className="font-medium">Purpose:</span> {remarksVisit.purpose}</div>}
                {remarksVisit.remarks && <div className="text-slate-500 mt-0.5"><span className="font-medium">Remarks:</span> {remarksVisit.remarks}</div>}
              </div>
              <div>
                <Label className="text-xs">Post-Meeting Notes</Label>
                <Textarea
                  value={remarksText}
                  onChange={e => setRemarksText(e.target.value)}
                  placeholder="e.g., Client showed interest in 3-year AMC, follow up next Monday..."
                  rows={4}
                  data-testid="visit-remarks-textarea"
                />
              </div>
              <div>
                <Label className="text-xs">Potential of Customer (in MT / Value)</Label>
                <Input
                  value={potentialText}
                  onChange={e => setPotentialText(e.target.value)}
                  placeholder="e.g., 50 MT / ₹5,00,000"
                  data-testid="visit-potential-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">Estimated demand — quantity in metric tons or value.</p>
              </div>
              <div>
                <Label className="text-xs">Outcome / Result</Label>
                <Textarea
                  value={outcomeText}
                  onChange={e => setOutcomeText(e.target.value)}
                  placeholder="e.g., Confirmed order for 2 units. Quotation shared."
                  rows={3}
                  data-testid="visit-outcome-input"
                />
              </div>
            </div>
          )}
          <div className="px-6 pb-6 pt-2 shrink-0 border-t border-slate-100 flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setRemarksOpen(false)} data-testid="cancel-remarks-btn">Cancel</Button>
            <Button size="sm" onClick={handleSaveRemarks} disabled={remarksSaving} className="bg-[#0D2850] hover:bg-[#0B2246]" data-testid="save-remarks-btn">
              {remarksSaving ? 'Saving…' : 'Save Details'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Log Visit Dialog */}
      <Dialog open={visitOpen} onOpenChange={setVisitOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-slate-100"><DialogTitle>Log Client Visit</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 overflow-y-auto px-6 py-4">
            <div><Label>Company Name <span className="text-red-500">*</span></Label>
              <Input value={visitForm.company_name} onChange={e => setVisitForm({...visitForm, company_name: e.target.value})} placeholder="e.g. ABC Industries" data-testid="visit-client-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer Name (person visited)</Label>
                <Input value={visitForm.customer_name} onChange={e => setVisitForm({...visitForm, customer_name: e.target.value})} placeholder="e.g. Rajesh Kumar" data-testid="visit-customer-name-input" />
              </div>
              <div><Label>Contact No. of Person Visited</Label>
                <Input value={visitForm.contact_number} onChange={e => setVisitForm({...visitForm, contact_number: e.target.value})} placeholder="e.g. +91 98765 43210" data-testid="visit-contact-input" />
              </div>
            </div>
            <div><Label>City / Town of Visit</Label>
              <Input value={visitForm.city_town} onChange={e => setVisitForm({...visitForm, city_town: e.target.value})} placeholder="e.g. Bhubaneswar" data-testid="visit-city-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tour Date</Label><Input type="date" value={visitForm.visit_date} onChange={e => setVisitForm({...visitForm, visit_date: e.target.value})} data-testid="visit-date-input" /></div>
              <div><Label>Time</Label><Input type="time" value={visitForm.visit_time} onChange={e => setVisitForm({...visitForm, visit_time: e.target.value})} data-testid="visit-time-input" /></div>
            </div>
            <div><Label>Journey Plan (optional)</Label>
              <Select value={visitForm.plan_id} onValueChange={v => setVisitForm({...visitForm, plan_id: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="visit-plan-select"><SelectValue placeholder="Link to a plan (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No plan</SelectItem>
                  {expensePlans.map(p => <SelectItem key={p.id} value={p.id}>{p.title} ({p.start_date})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Purpose</Label>
              <Textarea value={visitForm.purpose} onChange={e => setVisitForm({...visitForm, purpose: e.target.value})} rows={2} placeholder="Purpose of the visit" data-testid="visit-purpose-input" />
            </div>
            <div><Label>Remarks / Discussion</Label>
              <Textarea value={visitForm.remarks} onChange={e => setVisitForm({...visitForm, remarks: e.target.value})} rows={2} placeholder="Discussion points, initial observations" data-testid="visit-remarks-input" />
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-slate-500">GPS Location: </span>
                  {visitForm.location?.lat ? (
                    <span className="text-emerald-600 font-medium">{visitForm.location.lat.toFixed(4)}, {visitForm.location.lng.toFixed(4)}</span>
                  ) : (
                    <span className="text-slate-400">Not captured</span>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={captureGPS} disabled={gpsLoading} data-testid="visit-gps-btn">
                  <Navigation className="w-3.5 h-3.5 mr-1" /> {gpsLoading ? 'Capturing...' : 'Refresh GPS'}
                </Button>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6 pt-2 shrink-0 border-t border-slate-100">
            <Button onClick={handleCreateVisit} className="w-full bg-[#0D2850]" data-testid="visit-submit-btn">Log Visit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
